// The desk's reading of the market (read_market): what the Market Flow board computes, from the same
// session, members and snapshot, so the desk and the board always agree. Shared with the desk evals.
import { NSE, NSE_INDICES, snapshot, type IndexDef, type Session } from '@planb/core';

/** The index for an id ("NIFTY 500", "nifty500" …), else the board's default (the F&O list). */
export const marketIndex = (id: string): IndexDef =>
  NSE_INDICES.find((i) => i.id === id.toUpperCase().replace(/\s+/g, '')) ?? NSE_INDICES.find((i) => i.id === 'FNO')!;

export function marketReading(session: Session, ix: IndexDef) {
  const members = new Set(ix.members);
  const board = { ...session, stocks: session.stocks.filter((x) => members.has(x.symbol)) };
  const t = Math.max(0, ...board.stocks.map((x) => x.bars.length));
  if (!board.stocks.length || !t) return { error: `No ${ix.name} session data yet.` };
  const snap = snapshot(NSE, board, t);
  const pct = (x: number) => +x.toFixed(2);
  const up = snap.stocks.filter((x) => x.pct > 0).length, down = snap.stocks.filter((x) => x.pct < 0).length;
  const stock = (x: (typeof snap.stocks)[number]) => ({ symbol: x.symbol, sector: x.sector, changePct: pct(x.pct), volumeVsUsual: `${x.relVol.toFixed(2)}×` });
  const byPct = [...snap.stocks].sort((p, q) => q.pct - p.pct);
  return {
    source: 'MoneyHQ Market Flow board (live Kite data)', index: ix.name, session: session.date, asOf: snap.time, stocks: snap.stocks.length,
    breadth: { up, down, unchanged: snap.stocks.length - up - down },
    sectorsStrongestFirst: snap.sectors.map((x) => ({ sector: x.name, avgMovePct: pct(x.avg), buyersInControl: `${x.adv} of ${x.n}` })),
    strongest: byPct.slice(0, 5).map(stock), weakest: byPct.slice(-5).reverse().map(stock),
    howToRead: 'Sectors are ranked by their stocks\' average move today; "buyers in control" counts stocks the board reads as bullish. Say the index and time it is for.',
  };
}
